import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import axios from "axios";
import { Button, Card, Checkbox, Col, InputNumber, Layout, Popover, Row, Select, Space, Table, Typography } from "antd";
import { formatNumber } from "../../../util/NumberFormatter";
import MobileStatBox from "../../common/MobileStatBox";
import useIsMobile from "../../../util/useIsMobile";
import { computeAverages, getOutlierStyle } from "./statMetrics";

dayjs.extend(customParseFormat);
dayjs.locale('ko');

const tierOrder = ['Normal', 'Iron', 'Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond', 'Meteorite', 'Mithril'];
const tierDisplayMap = {
  'Mithril': '미스릴+',
  'Meteorite': '메테오라이트+',
  'Diamond': '다이아몬드+',
  'Platinum': '플래티넘+',
  'Gold': '골드+',
  'Silver': '실버+',
  'Bronze': '브론즈+',
  'Iron': '아이언+',
  'Normal': '전체(일반포함)'
};
const tierDropdownOptions = tierOrder.slice().reverse().map(tier => {
  const imagePath = `/img/tier/${tier}.png`;
  return {
    value: tier,
    label: (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <img
          src={imagePath}
          alt={tier}
          style={{
            height: '20px',
            width: 'auto',
            marginRight: '8px'
          }}
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
        <span>{tierDisplayMap[tier]}</span>
      </div>
    ),
  };
});

function MobileRankCard({ stat, index, averages, totalGameCount, onCharacterClick }) {
  const characterImgSrc = `/img/character/${stat.characterName}.png`;
  const weaponImgSrc = `/img/weapon_skill/${stat.bestWeapon}.png`;
  const tierImgSrc = `/img/character_tier/${stat.characterTier}.svg`;
  const pickRate = totalGameCount > 0 ? (stat.gameCount / totalGameCount) * 100 : 0;
  const hasGames = stat.gameCount > 0;
  const rpGain = hasGames ? stat.mmrGain / stat.gameCount : null;
  const avgRank = hasGames ? stat.gameRank / stat.gameCount : null;
  const winRate = hasGames ? (stat.victory / stat.gameCount) * 100 : 0;
  const top3Rate = hasGames ? (stat.top3 / stat.gameCount) * 100 : 0;
  const dmgPerGame = hasGames ? stat.damageToPlayer / stat.gameCount : 0;
  const dps = stat.totalTime > 0 ? stat.damageToPlayer / stat.totalTime : 0;
  const killsPerGame = hasGames ? stat.playerKill / stat.gameCount : 0;
  const tkPerGame = hasGames ? stat.teamKill / stat.gameCount : 0;

  return (
    <Card size="small" style={{ marginBottom: 8 }} bodyStyle={{ padding: 10 }}>
      <div
        style={{ display: 'flex', alignItems: 'center', marginBottom: 8, cursor: 'pointer' }}
        onClick={onCharacterClick}
      >
        <div style={{ width: 22, textAlign: 'center', fontWeight: 'bold', flexShrink: 0 }}>
          {index + 1}
        </div>
        <div style={{ position: 'relative', width: '32px', height: '32px', margin: '0 8px', flexShrink: 0 }}>
          <div style={{
            width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#d6d6d6',
            overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <img
              src={characterImgSrc}
              alt={stat.characterName}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
          <img
            src={weaponImgSrc}
            alt={stat.bestWeapon}
            style={{
              position: 'absolute', bottom: '0px', right: '-6px', width: '14px', height: '14px',
              borderRadius: '50%', backgroundColor: '#323232', padding: '2px', border: '1px solid #4A4A4A',
            }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
        <span style={{ flex: 1, color: '#1890ff', fontSize: '13px' }}>
          {stat.characterName} [{stat.bestWeapon}]
        </span>
        <div style={{
          width: '28px', height: '28px', borderRadius: '50%', backgroundColor: '#ffffff',
          overflow: 'hidden', flexShrink: 0, boxShadow: '0 0 0 1px #f0f0f0',
        }}>
          <img
            src={tierImgSrc}
            alt={stat.characterTier}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      </div>
      <Row gutter={[4, 6]}>
        <Col span={6}><MobileStatBox label="픽률" value={`${pickRate.toFixed(2)}%`} /></Col>
        <Col span={6}>
          <MobileStatBox
            label="승률"
            value={`${winRate.toFixed(2)}%`}
            style={getOutlierStyle(winRate / 100, averages.avgWinRate, false)}
          />
        </Col>
        <Col span={6}><MobileStatBox label="Top3" value={`${top3Rate.toFixed(2)}%`} /></Col>
        <Col span={6}>
          <MobileStatBox
            label="RP"
            value={rpGain === null ? '-' : rpGain.toFixed(2)}
            style={rpGain === null ? undefined : getOutlierStyle(rpGain, averages.avgMmrGain, false, 0.2)}
          />
        </Col>
        <Col span={6}>
          <MobileStatBox
            label="평균등수"
            value={avgRank === null ? '-' : avgRank.toFixed(2)}
            style={avgRank === null ? undefined : getOutlierStyle(avgRank, averages.avgRank, true, 0.05)}
          />
        </Col>
        <Col span={6}><MobileStatBox label="킬" value={killsPerGame.toFixed(2)} /></Col>
        <Col span={6}><MobileStatBox label="TK" value={tkPerGame.toFixed(2)} /></Col>
        <Col span={6}><MobileStatBox label="딜량" value={formatNumber(Math.floor(dmgPerGame))} /></Col>
        <Col span={6}><MobileStatBox label="dps" value={dps.toFixed(2)} /></Col>
      </Row>
    </Card>
  );
}

export default function TopRanker() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState([]);
  const [totalGameCount, setTotalGameCount] = useState(0);
  const [selectedMinTier, setSelectedMinTier] = useState('Diamond');
  const [mainWeathers, setMainWeathers] = useState({ '흐림': true, '쾌청': true, '비': true, '모래바람': true });
  const [subWeathers, setSubWeathers] = useState({ '무풍': true, '안개': true, '강풍': true, '벼락': true });
  const [daysAgo, setDaysAgo] = useState(7);
  const [popoverVisible, setPopoverVisible] = useState(false);
  const isMobile = useIsMobile();

  const handleMinTierChange = (value) => setSelectedMinTier(value);
  const handleMainWeatherChange = (mainWeather) => setMainWeathers(prev => ({ ...prev, [mainWeather]: !prev[mainWeather] }));
  const handleSubWeatherChange = (subWeather) => setSubWeathers(prev => ({ ...prev, [subWeather]: !prev[subWeather] }));

  const handleDaysChange = (newDays) => {
    const validatedDays = (typeof newDays === 'number' && newDays > 0) ? newDays : 3;
    setDaysAgo(validatedDays);
  };

  const handlePresetClick = (presetDays) => {
    handleDaysChange(presetDays);
    setPopoverVisible(false);
  };

  const popoverContent = (
    <Space direction="vertical" style={{ width: '250px' }}>
      <InputNumber
        min={1}
        max={31}
        value={daysAgo}
        onChange={(value) => {
          handleDaysChange(value === null ? 1 : value);
        }}
        addonAfter="일 전"
        style={{ width: '100%' }}
      />
      <Space wrap>
        <Button onClick={() => handlePresetClick(1)}>하루 전</Button>
        <Button onClick={() => handlePresetClick(3)}>3일 전</Button>
        <Button onClick={() => handlePresetClick(7)}>1주 전</Button>
        <Button onClick={() => handlePresetClick(14)}>2주 전</Button>
        <Button onClick={() => handlePresetClick(31)}>한 달 전</Button>
      </Space>
    </Space>
  );

  useEffect(() => {
    setLoading(true);
    let main = Object.keys(mainWeathers).filter(weatherKey => mainWeathers[weatherKey]).join(',')
    let sub = Object.keys(subWeathers).filter(weatherKey => subWeathers[weatherKey]).join(',')

    axios.get("/api/external/bser/top-ranker-stats", {
      params: {
        days: daysAgo,
        tier: selectedMinTier,
        mainWeathers: main,
        subWeathers: sub
      }
    })
      .then(r => r.data)
      .then(data => {
        const sortedStats = Object.values(data).sort((a, b) => b.score - a.score);
        setStats(sortedStats)
      })
      .finally(() => setLoading(false));
  }, [daysAgo, selectedMinTier, mainWeathers, subWeathers]);

  useEffect(() => {
    setTotalGameCount(stats.reduce((sum, stat) => sum + stat.gameCount, 0));
  }, [stats]);

  const averages = useMemo(() => computeAverages(stats), [stats]);

  const columns = [
    {
      title: '순위',
      align: 'center',
      render: (text, record, index) => <span style={{ textAlign: 'center' }}>{index + 1}</span>,
      fixed: 'left'
    },
    {
      title: '실험체',
      fixed: 'left',
      render: (stat) => {
        const characterImgSrc = `/img/character/${stat.characterName}.png`;
        const weaponImgSrc = `/img/weapon_skill/${stat.bestWeapon}.png`;

        const handleCharacterClick = () => {
          navigate(`/bser/character-stats?characterName=${encodeURIComponent(stat.characterName)}&bestWeapon=${encodeURIComponent(stat.bestWeapon)}`);
        };

        return (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              textAlign: 'left',
              cursor: 'pointer'
            }}
            onClick={handleCharacterClick}
          >
            <div style={{
              position: 'relative',
              width: '36px',
              height: '36px',
              marginRight: '8px',
              flexShrink: 0,
            }}>
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                backgroundColor: '#d6d6d6',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <img
                  src={characterImgSrc}
                  alt={stat.characterName}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <img
                src={weaponImgSrc}
                alt={stat.bestWeapon}
                style={{
                  position: 'absolute',
                  bottom: '0px',
                  right: '-8px',
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  backgroundColor: '#323232',
                  padding: '2px',
                  border: '1px solid #4A4A4A',
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
            <span style={{ whiteSpace: 'nowrap', color: '#1890ff' }}>
              {stat.characterName} [{stat.bestWeapon}]
            </span>
          </div>
        );
      }
    },
    {
      title: '픽 수',
      align: 'center',
      dataIndex: 'gameCount',
      sorter: (a, b) => a.gameCount - b.gameCount,
      render: (gameCount) => (
        <span style={{ textAlign: 'center' }}>
          <div>
            {totalGameCount > 0 ? ((gameCount / totalGameCount) * 100).toFixed(2) : 0}%
          </div>
          <div style={{ fontSize: '0.77em' }}>
            ({formatNumber(gameCount)})
          </div>
        </span>
      )
    },
    {
      title: 'score',
      align: 'center',
      sorter: (a, b) => a.score - b.score,
      render: (stat) => {
        const src = `/img/character_tier/${stat.characterTier}.svg`

        return (<div style={{
            display: 'flex',
            justifyContent: 'center', // 가운데 정렬로 변경
            alignItems: 'center',
            textAlign: 'center'
          }}>
            <div style={{
              position: 'relative',
              width: '36px',
              height: '36px',
              marginRight: '8px',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center', // 이 부분 추가
            }}>
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                backgroundColor: '#ffffff',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                {src && (
                  <img
                    src={src}
                    alt={stat.characterTier}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        );
      }
    },
    {
      title: 'RP 획득량',
      align: 'center',
      sorter: (a, b) => (a.gameCount > 0 ? a.mmrGain / a.gameCount : Infinity) - (b.gameCount > 0 ? b.mmrGain / b.gameCount : Infinity),
      render: (stat) => {
        const currentValue = stat.mmrGain / stat.gameCount;
        const displayValue = currentValue.toFixed(2);
        const style = getOutlierStyle(currentValue, averages.avgMmrGain, false, 0.2);
        return <span style={{ ...style, textAlign: 'center' }}>{displayValue}</span>;
      }
    },
    {
      title: '평균 등수',
      align: 'center',
      sorter: (a, b) => (a.gameCount > 0 ? a.gameRank / a.gameCount : Infinity) - (b.gameCount > 0 ? b.gameRank / b.gameCount : Infinity),
      render: (stat) => {
        if (stat.gameCount === 0) {
          return '-';
        }
        const currentValue = stat.gameRank / stat.gameCount;
        const displayValue = currentValue.toFixed(2);
        const style = getOutlierStyle(currentValue, averages.avgRank, true, 0.05);
        return <span style={{ ...style, textAlign: 'center' }}>{displayValue}</span>;
      }
    },
    {
      title: '승률',
      align: 'center',
      sorter: (a, b) => (a.gameCount > 0 ? a.victory / a.gameCount : 0) - (b.gameCount > 0 ? b.victory / b.gameCount : 0),
      render: (stat) => {
        let percentageDisplay;
        let absoluteValueDisplay;
        let currentValueForStyle;

        if (stat.gameCount === 0) {
          percentageDisplay = "0.00%";
          absoluteValueDisplay = stat.victory;
          currentValueForStyle = 0;
        } else {
          const winRate = stat.victory / stat.gameCount;
          percentageDisplay = `${(winRate * 100).toFixed(2)}%`;
          absoluteValueDisplay = stat.victory;
          currentValueForStyle = winRate;
        }
        const style = getOutlierStyle(currentValueForStyle, averages.avgWinRate, false);

        return (
          <span style={{ ...style, textAlign: 'center' }}>
            <div>{percentageDisplay}</div>
        <div style={{ fontSize: '0.77em' }}>
          ({absoluteValueDisplay})
        </div>
      </span>
        );
      }
    },
    {
      title: 'Top 3',
      align: 'center',
      sorter: (a, b) => (a.gameCount > 0 ? a.top3 / a.gameCount : 0) - (b.gameCount > 0 ? b.top3 / b.gameCount : 0),
      render: (stat) => {
        let percentageDisplay;
        let absoluteValueDisplay;
        let currentValueForStyle;

        if (stat.gameCount === 0) {
          percentageDisplay = "0.00%";
          absoluteValueDisplay = stat.top3;
          currentValueForStyle = 0;
        } else {
          const winRate = stat.top3 / stat.gameCount;
          percentageDisplay = `${(winRate * 100).toFixed(2)}%`;
          absoluteValueDisplay = stat.top3;
          currentValueForStyle = winRate;
        }
        const style = getOutlierStyle(currentValueForStyle, averages.avgTop3Rate, false);
        return (
          <span style={{ ...style, textAlign: 'center' }}>
            <div>{percentageDisplay}</div>
        <div style={{ fontSize: '0.77em' }}>
          ({absoluteValueDisplay})
        </div>
      </span>
        );
      }
    },
    {
      title: '딜량 [dps]',
      align: 'center',
      sorter: (a, b) => (a.gameCount > 0 ? a.damageToPlayer / a.gameCount : 0) - (b.gameCount > 0 ? b.damageToPlayer / b.gameCount : 0),
      render: (stat) => {
        if (stat.gameCount === 0) {
          return '- [-]';
        }
        const damagePerGame = stat.damageToPlayer / stat.gameCount;
        const dps = stat.totalTime > 0 ? (stat.damageToPlayer / stat.totalTime) : 0;
        return <span style={{ textAlign: 'center' }}>{formatNumber(Math.floor(damagePerGame))} [{dps.toFixed(2)}]</span>;
      }
    },
    {
      title: '킬 수',
      align: 'center',
      sorter: (a, b) => (a.gameCount > 0 ? a.playerKill / a.gameCount : 0) - (b.gameCount > 0 ? b.playerKill / b.gameCount : 0),
      render: (stat) => {
        if (stat.gameCount === 0) {
          return '-';
        }
        return <span style={{ textAlign: 'center' }}>{(stat.playerKill / stat.gameCount).toFixed(2)}</span>;
      }
    },
    {
      title: 'TK',
      align: 'center',
      sorter: (a, b) => (a.gameCount > 0 ? a.teamKill / a.gameCount : 0) - (b.gameCount > 0 ? b.teamKill / b.gameCount : 0),
      render: (stat) => {
        if (stat.gameCount === 0) {
          return '-';
        }
        return <span style={{ textAlign: 'center' }}>{(stat.teamKill / stat.gameCount).toFixed(2)}</span>;
      }
    },
  ];

  const handleTableChange = (pagination, filters, sorter) => {
  };

  const weatherFilterContent = (
    <Space direction="vertical" size="middle">
      <div>
        <Typography.Text strong>주 날씨 : </Typography.Text>
        <div style={{ marginTop: 8 }}>
          <Space wrap>
            {Object.keys(mainWeathers).map(mainWeather => (
              <Checkbox
                key={mainWeather}
                checked={mainWeathers[mainWeather]}
                onChange={() => handleMainWeatherChange(mainWeather)}
              >
                {mainWeather}
              </Checkbox>
            ))}
          </Space>
        </div>
      </div>
      <div>
        <Typography.Text strong>보조 날씨 : </Typography.Text>
        <div style={{ marginTop: 8 }}>
          <Space wrap>
            {Object.keys(subWeathers).map(subWeather => (
              <Checkbox
                key={subWeather}
                checked={subWeathers[subWeather]}
                onChange={() => handleSubWeatherChange(subWeather)}
              >
                {subWeather}
              </Checkbox>
            ))}
          </Space>
        </div>
      </div>
    </Space>
  );

  return (
    <Layout style={{ background: '#ffffff' }}>
      <Layout.Content style={{ padding: '20px' }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: '20px', rowGap: 8 }}>
            <Space direction="horizontal" align="center" wrap>
              <Popover
                content={popoverContent}
                title={<Typography.Text strong>기간 선택</Typography.Text>}
                trigger="click"
                open={popoverVisible}
                onOpenChange={setPopoverVisible}
              >
                <Button style={{ minWidth: '110px', textAlign: 'center' }}>
                  {`${daysAgo}일 전`}
                </Button>
              </Popover>
              <Select
                value={selectedMinTier}
                style={{ width: 140 }}
                onChange={handleMinTierChange}
                options={tierDropdownOptions}
                listHeight={288}
              />
              <Popover content={weatherFilterContent} title={<Typography.Text strong>날씨 필터 설정</Typography.Text>} trigger="click"
                       placement="bottom">
                <Button>날씨 필터</Button>
              </Popover>
            </Space>
            <div style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
              총 픽 수: {formatNumber(totalGameCount)}
            </div>
          </div>
          {isMobile ? (
            <div>
              {stats.map((stat, index) => (
                <MobileRankCard
                  key={`${stat.characterName}-${stat.bestWeapon}`}
                  stat={stat}
                  index={index}
                  averages={averages}
                  totalGameCount={totalGameCount}
                  onCharacterClick={() => navigate(`/bser/character-stats?characterName=${encodeURIComponent(stat.characterName)}&bestWeapon=${encodeURIComponent(stat.bestWeapon)}`)}
                />
              ))}
            </div>
          ) : (
            <Table
              className='table-wrapper-with-border'
              pagination={false}
              dataSource={stats}
              columns={columns}
              onChange={handleTableChange}
              scroll={{ x: 'max-content' }}
              loading={loading}
              rowKey={(record) => `${record.characterName}-${record.bestWeapon}`}
            />
          )}
        </Space>
      </Layout.Content>
    </Layout>
  );
}
